import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SessionExpiredModal from '../components/SessionExpiredModal';
import InactivityWarningModal from '../components/InactivityWarningModal';

const AuthContext = createContext();

// Configuration: Read from environment or use defaults
const INACTIVITY_TIMEOUT = parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT || '30', 10) * 60 * 1000; // 30 minutes
const WARNING_BEFORE_LOGOUT = parseInt(process.env.REACT_APP_INACTIVITY_WARNING || '5', 10) * 60 * 1000; // 5 minutes
const AUTO_REFRESH_ENABLED = process.env.REACT_APP_AUTO_REFRESH_TOKEN !== 'false'; // default true

// Session ID management using window.name (persists through reloads, cleared on tab close)
// This allows us to distinguish between page reload (keep session) and restored tab (clear session)
const getOrCreateTabSessionId = () => {
  // Check if we already have a session ID in window.name (survives reloads)
  if (window.name && window.name.startsWith('tab_session_')) {
    return window.name;
  }
  // Generate new session ID for this tab
  const newSessionId = `tab_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  window.name = newSessionId;
  return newSessionId;
};

const TAB_SESSION_ID = getOrCreateTabSessionId();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  
  // Session management state
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Dynamic backend URL detection based on environment
  const getBackendUrl = () => {
    console.log('[AuthContext] Detecting backend URL...');
    console.log('[AuthContext] REACT_APP_BACKEND_URL:', window.location.origin);
    console.log('[AuthContext] Current hostname:', window.location.hostname);
    
    // PRIORITY 1: Detect based on current hostname (RUNTIME)
    const hostname = window.location.hostname;
    
    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      console.log('[AuthContext] Local development detected, using:', 'http://localhost:8001');
      return 'http://localhost:8001';
    }
    
    // All other domains (production, preview, emergent.host, etc.)
    // Use window.location.origin for dynamic URL detection
    console.log('[AuthContext] Using origin URL:', window.location.origin);
    return window.location.origin;
  };

  const backendUrl = getBackendUrl();
  console.log('[AuthContext] Final backend URL:', backendUrl);

  // ============================================
  // PHASE 1: Token Expiration Check
  // ============================================
  
  /**
   * Decode JWT token and check if it's expired
   * @param {string} token - JWT token to check
   * @returns {boolean} - True if expired, false otherwise
   */
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      // Decode JWT (without verification - just reading exp claim)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const decoded = JSON.parse(jsonPayload);
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Check if token has expired (with 30 second buffer)
      return decoded.exp < (currentTime + 30);
    } catch (error) {
      console.error('[AuthContext] Error decoding token:', error);
      return true; // Treat invalid tokens as expired
    }
  };

  /**
   * Get decoded token data
   * @param {string} token - JWT token to decode
   * @returns {object|null} - Decoded token data or null
   */
  const getDecodedToken = (token) => {
    if (!token) return null;
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('[AuthContext] Error decoding token:', error);
      return null;
    }
  };

  // Initialize token from sessionStorage on component mount
  // Validates that this is the same tab session (not a restored tab from history)
  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    const storedSessionId = sessionStorage.getItem('tab_session_id');
    
    console.log('[AuthContext] Initializing...');
    console.log('[AuthContext] Stored token:', storedToken ? `${storedToken.substring(0, 20)}...` : 'null');
    console.log('[AuthContext] Stored session ID:', storedSessionId);
    console.log('[AuthContext] Current tab session ID:', TAB_SESSION_ID);
    
    if (storedToken && storedSessionId === TAB_SESSION_ID) {
      // Same tab session - token is valid
      console.log('[AuthContext] Valid session, restoring token');
      setToken(storedToken);
    } else if (storedToken && !storedSessionId) {
      // Token exists but no session ID - this is a fresh login redirect, accept it
      // Store our session ID for future validation
      console.log('[AuthContext] Fresh login detected, accepting token and setting session ID');
      sessionStorage.setItem('tab_session_id', TAB_SESSION_ID);
      setToken(storedToken);
    } else if (storedToken && storedSessionId !== TAB_SESSION_ID) {
      // Token exists but session ID mismatch - restored tab from history
      console.log('[AuthContext] Restored tab detected (session ID mismatch), clearing session');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('temp_token');
      sessionStorage.removeItem('tab_session_id');
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    console.log('[AuthContext] Fetching current user with token:', token ? `${token.substring(0, 20)}...` : 'null');
    try {
      const response = await fetch(`${backendUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('[AuthContext] Response status:', response.status);

      if (response.ok) {
        const userData = await response.json();
        console.log('[AuthContext] User data received:', userData);
        setUser(userData);
        
        // Set default platform - Admin users get "All Platforms", regular users get their first platform
        if (userData.is_admin) {
          // Admin users always default to "All Platforms"
          const savedPlatform = localStorage.getItem('selectedPlatform');
          if (savedPlatform) {
            setSelectedPlatform(savedPlatform);
          } else {
            setSelectedPlatform('all');
            localStorage.setItem('selectedPlatform', 'all');
          }
        } else if (userData.platforms && userData.platforms.length > 0) {
          // Regular users default to their first assigned platform
          const savedPlatform = localStorage.getItem('selectedPlatform');
          if (savedPlatform && userData.platforms.includes(savedPlatform)) {
            setSelectedPlatform(savedPlatform);
          } else {
            setSelectedPlatform(userData.platforms[0]);
            localStorage.setItem('selectedPlatform', userData.platforms[0]);
          }
        }
      } else {
        console.log('[AuthContext] Response not ok, logging out');
        logout();
      }
    } catch (error) {
      console.error('[AuthContext] Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    console.log('[AuthContext] Attempting login with:', email);
    
    // Clear any existing authentication state before starting new login
    setToken(null);
    setUser(null);
    setSelectedPlatform(null);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('temp_token');
    localStorage.removeItem('selectedPlatform');
    
    // Small delay to ensure state clearing is processed
    await new Promise(resolve => setTimeout(resolve, 50));
    
    try {
      // Use the MFA login endpoint which handles both MFA and non-MFA users
      const response = await fetch(`${backendUrl}/api/mfa/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          remember_device: false
        }),
      });

      console.log('[AuthContext] Login response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('[AuthContext] Login response data:', data);

        if (data.requires_mfa_setup) {
          // Store temp token for MFA setup and redirect
          sessionStorage.setItem('temp_token', data.access_token);
          sessionStorage.setItem('tab_session_id', TAB_SESSION_ID);
          return { success: false, requiresMFASetup: true, user: data.user };
        } else if (data.requires_mfa) {
          // Redirect to MFA verification
          return { success: false, requiresMFA: true };
        } else {
          // Complete login
          console.log('[AuthContext] Login successful, setting token and user');
          
          // Clear any cached platform data first
          setSelectedPlatform(null);
          localStorage.removeItem('selectedPlatform');
          
          sessionStorage.setItem('token', data.access_token);
          sessionStorage.setItem('tab_session_id', TAB_SESSION_ID);
          setToken(data.access_token);
          setUser(data.user);
          
          // Set platform for admin users after clearing
          if (data.user.is_admin) {
            setSelectedPlatform('all');
            localStorage.setItem('selectedPlatform', 'all');
          } else if (data.user.platforms && data.user.platforms.length > 0) {
            setSelectedPlatform(data.user.platforms[0]);
            localStorage.setItem('selectedPlatform', data.user.platforms[0]);
          }
          
          return { success: true };
        }
      } else {
        const errorData = await response.json();
        console.log('[AuthContext] Login failed:', errorData);
        return { success: false, error: errorData.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('[AuthContext] Login error:', error);
      return { success: false, error: 'Network error occurred' };
    }
  };

  // ============================================
  // PHASE 3: Auto Token Refresh
  // ============================================
  
  /**
   * Refresh the access token
   * @returns {Promise<boolean>} - True if successful, false otherwise
   */
  const refreshToken = async () => {
    if (!token) return false;
    
    try {
      console.log('[AuthContext] Attempting to refresh token...');
      const response = await fetch(`${backendUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[AuthContext] Token refreshed successfully');
        sessionStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        if (data.user) {
          setUser(data.user);
        }
        return true;
      } else {
        console.log('[AuthContext] Token refresh failed:', response.status);
        return false;
      }
    } catch (error) {
      console.error('[AuthContext] Token refresh error:', error);
      return false;
    }
  };

  const logout = (showModal = false) => {
    console.log('[AuthContext] Logging out user');
    
    // Preserve theme data before clearing
    const themeData = localStorage.getItem('app_theme');
    
    // Clear all state immediately
    setToken(null);
    setUser(null);
    setSelectedPlatform(null);
    setShowInactivityWarning(false);
    setLoading(false);
    
    // Clear session token storage (including session ID)
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('temp_token');
    sessionStorage.removeItem('tab_session_id');
    
    // Clear localStorage preferences but preserve theme
    localStorage.removeItem('selectedPlatform');
    
    // Restore theme data after clearing
    if (themeData) {
      localStorage.setItem('app_theme', themeData);
    }
    
    // Show session expired modal if requested, otherwise redirect immediately
    if (showModal) {
      setShowSessionExpired(true);
      // Auto-navigate after modal is shown (handled by modal component)
    } else {
      navigate('/login', { replace: true });
    }
  };

  const switchPlatform = (platformId) => {
    // Admin users can switch to any platform or select "all", regular users are restricted to their assigned platforms
    if (user && (user.is_admin || user.platforms.includes(platformId))) {
      setSelectedPlatform(platformId);
      localStorage.setItem('selectedPlatform', platformId);
      console.log('[AuthContext] Platform switched to:', platformId);
    } else {
      console.log('[AuthContext] Platform switch denied. User access:', user?.platforms, 'Requested:', platformId);
    }
  };

  // ============================================
  // PHASE 1: Periodic Token Expiration Check
  // ============================================
  useEffect(() => {
    if (!token || !user) return;
    
    // Check token expiration every minute
    const checkInterval = setInterval(() => {
      if (isTokenExpired(token)) {
        console.log('[AuthContext] Token expired, showing modal and logging out');
        logout(true); // Show modal before redirecting
      }
    }, 60000); // Check every 60 seconds
    
    return () => clearInterval(checkInterval);
  }, [token, user]);

  // ============================================
  // PHASE 2: Activity Tracking & Inactivity Timeout
  // ============================================
  
  // Track user activity (throttled to avoid flooding setState on mousemove)
  useEffect(() => {
    if (!user) return;

    let lastFiredAt = 0;
    const THROTTLE_MS = 5000; // update at most once every 5s

    const updateActivity = () => {
      const now = Date.now();
      if (now - lastFiredAt < THROTTLE_MS) return;
      lastFiredAt = now;
      setLastActivity(now);
      setShowInactivityWarning((prev) => (prev ? false : prev));
    };

    // Listen to user activity events
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user]);
  
  // Check for inactivity
  useEffect(() => {
    if (!user) return;
    
    const checkInactivity = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      
      // Show warning 5 minutes before logout (or configured time)
      if (timeSinceActivity >= (INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT) && 
          timeSinceActivity < INACTIVITY_TIMEOUT &&
          !showInactivityWarning) {
        console.log('[AuthContext] Showing inactivity warning');
        setShowInactivityWarning(true);
      }
      
      // Logout after inactivity timeout
      if (timeSinceActivity >= INACTIVITY_TIMEOUT) {
        console.log('[AuthContext] Inactivity timeout, logging out');
        setShowInactivityWarning(false);
        logout(true); // Show session expired modal
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(checkInactivity);
  }, [user, lastActivity, showInactivityWarning]);

  // ============================================
  // PHASE 3: Auto Token Refresh
  // ============================================
  useEffect(() => {
    if (!token || !user || !AUTO_REFRESH_ENABLED) return;
    
    const checkAndRefresh = setInterval(async () => {
      const decoded = getDecodedToken(token);
      if (!decoded) return;
      
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = decoded.exp - currentTime;
      
      // Refresh if expiring in next 5 minutes (300 seconds)
      if (timeUntilExpiry < 300 && timeUntilExpiry > 0) {
        console.log('[AuthContext] Token expiring soon, refreshing...');
        const refreshed = await refreshToken();
        if (!refreshed) {
          console.log('[AuthContext] Token refresh failed, logging out');
          logout(true); // Show session expired modal
        }
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(checkAndRefresh);
  }, [token, user]);

  // Handle inactivity warning actions
  const handleStayLoggedIn = () => {
    console.log('[AuthContext] User chose to stay logged in');
    setLastActivity(Date.now());
    setShowInactivityWarning(false);
  };

  const handleLogoutFromWarning = () => {
    console.log('[AuthContext] User chose to logout from warning');
    setShowInactivityWarning(false);
    logout();
  };

  const value = {
    user,
    token,
    loading,
    selectedPlatform,
    backendUrl,
    login,
    logout,
    switchPlatform,
    refreshToken,
    isAdmin: user?.is_admin || false,
    isAuthenticated: !!token && !!user,
  };

  console.log('[AuthContext] Current state:', {
    hasUser: !!user,
    hasToken: !!token,
    loading,
    isAuthenticated: !!token && !!user
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Session Expired Modal */}
      {showSessionExpired && (
        <SessionExpiredModal onClose={() => {
          setShowSessionExpired(false);
          navigate('/login', { replace: true });
        }} />
      )}
      
      {/* Inactivity Warning Modal */}
      {showInactivityWarning && (
        <InactivityWarningModal
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={handleLogoutFromWarning}
          timeRemaining={WARNING_BEFORE_LOGOUT}
        />
      )}
    </AuthContext.Provider>
  );
};